import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import ViewportImage from '../image/viewport-image';
import Link from '@eliadmin/core/src/components/link';

import { Badge } from '../ui/badge';
import { HtmlDescription } from '../ui/html-description';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { GoArrowUpRight } from 'react-icons/go';
import type { Reference } from '@eliadmin/core/src/api/references/references-types';

export function ReferenceCard({
  reference,
  deferImage = false,
  headingTag: HeadingTag = 'h3',
}: {
  reference: Reference;
  deferImage?: boolean;
  headingTag?: 'h2' | 'h3';
}) {
  const CardImage = deferImage ? ViewportImage : Image;
  return (
    <Link
      prefetch={false}
      href={reference.slug}
      className={cn(
        'relative flex aspect-square h-full w-full flex-col items-start justify-end overflow-hidden rounded-[16px] p-6 sm:rounded-[20px] lg:rounded-[24px]',
        'group transition-all',
      )}
    >
      <div className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-black sm:right-5 sm:top-5 lg:right-6 lg:top-6 lg:h-[50px] lg:w-[50px]">
        <GoArrowUpRight size={24} />
      </div>
      <div className="relative z-[5] flex w-full flex-col items-start justify-start gap-2 sm:gap-3">
        <div className="flex items-start justify-start gap-1">
          {reference?.categories?.length > 0 && (
            <Badge size={'sm'}>{reference.categories[0]?.name}</Badge>
          )}
          {!!reference.city && (
            <Badge size={'sm'} variant={'white'}>
              {reference.city}
            </Badge>
          )}
        </div>
        <HeadingTag
          data-ea-visual-tag="h6"
          className="text-xl font-medium tracking-tight text-white sm:text-2xl"
        >
          {reference.name}
        </HeadingTag>
        <HtmlDescription
          color="white"
          content={reference.description}
          size="base"
          className={cn('line-clamp-2 max-h-12 w-full', 'ea-reference-card__description')}
        />
      </div>
      <div className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-reference-card__overlay')} />
      <CardImage
        src={reference.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(reference?.image, reference?.name)}
        width={0}
        height={0}
        className={cn(
          'absolute inset-0 z-0 h-full w-full object-cover object-center',
          'transition-all duration-200 group-hover:scale-110',
        )}
        sizes="(max-width: 500px) 100vw, 500px"
      />
    </Link>
  );
}
